import { Component, DetailEvent, Entity, registerComponent, registerSystem, System } from "aframe";
import { Matrix4, Object3D, Quaternion, QuaternionLike, Vector3, Vector3Like } from "three";
import { RTCClient, myself } from "../../rtc/client";
import { Guid, parseGuid } from "../../rtc/guid";
import { MessageData, ProtocolMessage } from "../../rtc/protocol";
import { untransformPoint } from "../../three/math";
import { ClipMap } from "../animation/clips";
import { TemplateSystem } from "../template";
import { whenLoaded } from "../utils";

export type RtcAvatarEntity = Entity< { [attrName:string] : Component } & { "rtc-avatar" : RtcAvatarComponent } > &
{
	rtc : RtcAvatarComponent;
};

export interface RtcAvatarSystem extends System
{
	data : {
		template? : string
		cameraSelector? : string;
		events : string[];
	};

	client? : RTCClient;
	protocol? : ControlCodes;
	
	avatars : { [id : string] : RtcAvatarEntity };
	getAvatar( id : Guid ) : RtcAvatarEntity;

	processMessage(message : MessageData) : void;
	//sendLocalAvatarProperties( name : string, title : string, model : string ) : void;
	sendLocalAvatarPosition( position : Vector3, orientation : number, velocity : Vector3, grounded : boolean ) : void;
	sendLocalAvatarAnimation( clips : ClipMap ) : void;
	sendLocalAvatarSkeleton( avatarEl : Entity, root:Object3D ) : void;

	sendEvent(type : string, detail : any) : void;
}

export interface RtcAvatarComponent extends Component
{
	el : RtcAvatarEntity;
	system : RtcAvatarSystem;
	data : { 
		id : Guid;
		camera? : Entity;
	};
	flags : number;
	velocity : Vector3;
	cameraPosition : Vector3;
	cameraDirection : Vector3;
	cameraRight : Vector3;
}
interface RtcAvatarComponentInternal extends RtcAvatarComponent
{
	processMessage(code : number, time : number, payload : Uint8Array) : void;
}


interface ControlCodes
{
	Info : ProtocolMessage;
	Transform : ProtocolMessage;
	Animation : ProtocolMessage;
	Skeleton : ProtocolMessage;
	Event : ProtocolMessage;
	Forget : ProtocolMessage;
	Disconnected : ProtocolMessage;
	Destroy : ProtocolMessage;
}

interface InfoPayload
{
	name : string;
	title : string;
	modelId : string;
}
interface TransformPayload
{
	position : Vector3Like;
	orientation : number;
	flags : number;
	velocity : Vector3Like;
	camera : {
		position : Vector3Like;
		direction : Vector3Like;
		right : Vector3Like;
	}
}
interface AnimationPayload
{
	animations : {
		filter : string;
		clips : string[];
	}[];
}
interface SkeletonPayload
{
	root : {
		name : string;
		position : Vector3Like;
		rotation : QuaternionLike;
	};
	bones : {
		name : string;
		rotation : QuaternionLike;
	}[];
}

interface EventPayload
{
	type : string;
	detail : string;
}





registerSystem("rtc-avatar", {

	schema : {
		template : { type:"string" },
		cameraSelector : { type : "string" },
		events : { type : "array", default : [] }
	},

	init(this: RtcAvatarSystem)
	{
		this.avatars = {};

		this.el.addEventListener("rtc-connected", (event) =>
		{
			this.client = (event as DetailEvent<RTCClient>).detail;

			const codes : { [name:string] : ProtocolMessage } = {};
			for(let pm of this.client.protocol!.avatar)
				codes[pm.name] = pm;
			this.protocol = codes as any;
			const firstCode = codes.Info!.code, lastCode = codes.Disconnected!.code;

			const onMessage = (event : DetailEvent<MessageData>) =>
				(event.detail.code >= firstCode) && (event.detail.code <= lastCode)
				&& !event.detail.emitter.equals(myself)
				&& this.processMessage(event.detail);

			this.client.addEventListener("message", onMessage as EventListener);
		});
	},

	update(this : RtcAvatarSystem)
	{
		if(this.data.template) //Preload template
		{
			const ts = this.el.systems.template as TemplateSystem;
			ts && ts.get(this.data.template);
		}
	},

	getAvatar(this : RtcAvatarSystem, guid : Guid ) : RtcAvatarEntity
	{
		const id = guid.toString();
		let entity = this.avatars[id];
		if(!entity)
		{
			this.avatars[id] = entity = this.el.ownerDocument.createElement("a-entity") as RtcAvatarEntity;
			entity.setAttribute("rtc-avatar", { id : guid } );
			entity.setAttribute("variables" , { avatar : new String(id) } );
			if(this.data.template)
			{
				if(this.data.cameraSelector)
				{
					// Wait for template-loaded event to make sure camera entity is selectable
					entity.addEventListener("template-loaded", (event) =>
					{
						entity.setAttribute("rtc-avatar", { camera : this.data.cameraSelector });
					}, { once : true } );
				}
				entity.setAttribute("template__rtc-avatar", this.data.template );
			} 
			this.el.appendChild(entity);
		}
		return entity;
	},

	processMessage(this : RtcAvatarSystem, message : MessageData)
	{
		const avatarEl = this.getAvatar(message.emitter);
		whenLoaded(avatarEl, (_) => {
			const rtcAC = avatarEl.components["rtc-avatar"] as RtcAvatarComponentInternal;
			rtcAC.processMessage(message.code, message.time, message.data );
			if(message.code >= this.protocol!.Disconnected.code) //Destroyed (permanently disconnected)
			{
				delete this.avatars[message.emitter.toString()];
				avatarEl.removeFromParent();
			}
		} );
	},

	/*
	sendLocalAvatarProperties(this : RtcAvatarSystem, name : string, title : string, model : string)
	{
		if(!this.client) return;

		const eName = encode(name || "");
		const eTitle = encode(title || "");
		const eModel = encode(model || "");

		this.client.sendMessage(this.protocol!.Info.code, eName, 0, eTitle, 0, eModel, 0);
	},
	*/

	sendLocalAvatarPosition(this : RtcAvatarSystem, position : Vector3, orientation : number, velocity : Vector3, grounded : boolean )
	{
		if(!this.client) return;

		const camMat = this.el.camera.el.object3D.matrixWorld.elements;
		const payload : TransformPayload =
		{
			position,
			orientation,
			velocity,
			flags : 0,
			camera : {
				position : { x : camMat[12], y : camMat[13], z : camMat[14] },
				direction : { x : -camMat[8], y : -camMat[9], z : -camMat[10] },
				right : { x : camMat[0], y : camMat[1], z : camMat[2] }
			}
		};

		if(grounded) payload.flags |= 0x1;

		this.client.sendAvatarMessage(this.protocol!.Transform, payload);
	},

	sendLocalAvatarAnimation(this : RtcAvatarSystem, clips : ClipMap )
	{
		if(!this.client) return;

		const payload : AnimationPayload = { animations : [] };
		for(let filter in clips)
		{
			payload.animations.push( {
				filter,
				clips : clips[filter]
			});
		}

		this.client.sendAvatarMessage(this.protocol!.Animation, payload);
	},

	sendLocalAvatarSkeleton(this : RtcAvatarSystem, avatarEl : Entity, root : Object3D )
	{
		if(!this.client) return;

		const payload : SkeletonPayload = 
		{
			root : { name:root.name, position:root.position, rotation:root.quaternion },
			bones : []
		};
		for(let child of root.children)
			traverseSkeleton(avatarEl, child, payload);

		this.client.sendAvatarMessage(this.protocol!.Skeleton, payload);
	},

	sendEvent(this : RtcAvatarSystem, type : string, detail : any)
	{
		if(!this.client) return;

		const payload : EventPayload = {
			type,
			detail : JSON.stringify(detail)
		};

		this.client.sendAvatarMessage(this.protocol!.Event, payload);
	}
})





const traverseSkeleton = (el : Entity, bone : Object3D & { el? : Entity, isBone? : boolean }, output : SkeletonPayload ) =>
{
	if(bone.el && (bone.el !== el))
		return;
	if(bone.isBone)
		output.bones.push( { name : bone.name, rotation : bone.quaternion });
	for(let child of bone.children)
		traverseSkeleton(el, child, output);
};

const traverseSkeletonBack = (el : Entity, obj : Object3D & { el? : Entity, isBone? : boolean }, quaternions : { [name:string] : QuaternionLike }) =>
{
	if(obj.el && (obj.el !== el))
		return;

	if(obj.isBone)
	{
		const q = quaternions[obj.name];
		q && obj.quaternion.copy(q);
	}
	for(let child of obj.children)
		traverseSkeletonBack(el, child, quaternions);
};

registerComponent("rtc-avatar", {
	dependencies : ["model-instance", "clips"],
	schema : {
		id : Guid.schema,
		camera : { type : "selector" }
	},

	init(this : RtcAvatarComponent)
	{
		this.el.rtc = this;

		this.flags = 0;
		this.velocity = new Vector3();
		this.cameraPosition = new Vector3();
		this.cameraDirection = new Vector3();
		this.cameraRight = new Vector3();

		this.el.emit("rtc-avatar-initialized", this);
	},
	
	tick(this : RtcAvatarComponent, t : number, dt : number)
	{
		dt *= 0.001; //In seconds.
		this.el.object3D.position.x += dt * this.velocity.x;
		if(this.flags & 0x01) //Grounded
			this.el.object3D.position.y += dt * this.velocity.y;
		else //Not grounded : apply gravity
		{
			this.el.object3D.position.y += dt * (this.velocity.y - 4.905 * dt);
			this.velocity.y -= 9.81 * dt;
		}
		this.el.object3D.position.z += dt * this.velocity.z;
	},
	
	processMessage(this : RtcAvatarComponent, code : number, time : number, payload : Uint8Array )
	{
		const client = this.system.client!;
		const /*code = message.code,*/ protocol = this.system.protocol!;
		const el = this.el;
		const obj = el.object3D;

		if( code >= protocol.Forget.code ) //Something bad happened...
		{
			obj.visible = false;
			return;
		}

		obj.visible = true;

		//const reader = new Reader(message.data);
		if( code == protocol.Info.code ) //Name/Title/Model
		{
			const data = client.deserialize(protocol.Info, payload) as InfoPayload;
			el.setAttribute("variables", { name : new String(data.name), title : new String(data.title) });
			el.setAttribute("model-instance", { model : data.modelId });
			return;
		}

		if( code == protocol.Transform.code ) //sync
		{
			const data : TransformPayload = 
			{
				position : obj.position,
				orientation : 0,
				flags : 0,
				velocity : this.velocity,
				camera : {
					position : this.cameraPosition,
					direction : this.cameraDirection,
					right : this.cameraRight
				}
			};
			client.deserialize(protocol.Transform, payload, null, data );
			obj.rotation.y = data.orientation;
			this.flags = data.flags;

			const cameraEl = this.data.camera;
			if(cameraEl)
			{
				const matrix = new Matrix4();
				matrix.elements.splice(0, 16,
					this.cameraRight.x,
					this.cameraRight.y,
					this.cameraRight.z,
					0,
					this.cameraRight.y * this.cameraDirection.z - this.cameraRight.z * this.cameraDirection.y,
					this.cameraRight.z * this.cameraDirection.x - this.cameraRight.x * this.cameraDirection.z,
					this.cameraRight.x * this.cameraDirection.y - this.cameraRight.y * this.cameraDirection.x,
					0,
					-this.cameraDirection.x,
					-this.cameraDirection.y,
					-this.cameraDirection.z,
					0,
					0,
					0,
					0,
					1
				);

				const camParent = cameraEl.object3D.parent!, q = new Quaternion(), q2 = new Quaternion();
				camParent.updateWorldMatrix(true, false);
				
				//q.setFromRotationMatrix( camParent.matrixWorld ).conjugate();
				//cameraEl.object3D.quaternion.setFromRotationMatrix(matrix).premultiply(q);
				camParent.matrixWorld.decompose( {} as Vector3, q, {} as Vector3 );
				matrix.decompose({} as Vector3, q2, {} as Vector3);
				cameraEl.object3D.quaternion.multiplyQuaternions(q.conjugate(), q2);
				cameraEl.object3D.position.copy(this.cameraPosition);
				untransformPoint(cameraEl.object3D.position, camParent.matrixWorld);
			}
			return;
		}

		if( code == protocol.Animation.code ) //Animation clips from non-vr avatar
		{
			const data = client.deserialize(protocol.Animation, payload) as AnimationPayload;
			const animations : ClipMap = {};
			for(let anim of data.animations)
				animations[ anim.filter ] = anim.clips;
			el.setAttribute("clips", animations);
			return;
		}

		if( code == protocol.Skeleton.code ) //Skeleton quaternions from vr avatar
		{
			const data = client.deserialize(protocol.Skeleton, payload) as SkeletonPayload;

			const rootObj = obj.getObjectByName(data.root.name);
			if(!rootObj) return;

			rootObj.position.copy( data.root.position );
			rootObj.quaternion.copy( data.root.rotation )

			const bones : { [name:string] : QuaternionLike } = {};
			for(let bone of data.bones)
			{
				bones[bone.name] = bone.rotation;
			}

			traverseSkeletonBack(el, rootObj, bones);
			return;
		}

		if( code == protocol.Event.code )
		{
			const data = client.deserialize(protocol.Event, payload) as EventPayload;
			el.emit( data.type, JSON.parse(data.detail), false);
			return;
		}
	}
} );


